/**
 * Snowcubes Analytics - PostHog wrapper with dev/prod separation
 */

// Event name constants
const ANALYTICS_EVENTS = {
  ADD_TO_CART: 'add_to_cart',
  QUICK_ADD_OPEN: 'quick_add_open',
  CART_QUANTITY_UPDATE: 'cart_quantity_update',
  NEWSLETTER_SIGNUP: 'newsletter_signup',
  CONTACT_FORM_SUBMIT: 'contact_form_submit',
  VARIANT_SELECT: 'variant_select',
  DELIVERY_DATE_SELECT: 'delivery_date_select',
  FLAVOR_SELECT: 'flavor_select',
  PRODUCT_VIEW: 'product_view',
  CHECKOUT_CLICK: 'checkout_click',
  PRODUCT_CARD_CLICK: 'product_card_click',
  CART_VIEW: 'cart_view',
  CONTINUE_SHOPPING_CLICK: 'continue_shopping_click',
  HOMEPAGE_VIEW: 'homepage_view',
  // Wedding favors funnel — see ai/plans/wedding-favors-2026/PAGE-REBUILD.md
  WEDDING_PAGE_VIEWED: 'wedding_page_viewed',
  WEDDING_CTA_CLICKED: 'wedding_cta_clicked',
  WEDDING_INQUIRY_SUBMITTED: 'wedding_inquiry_submitted',
  WEDDING_BOOKED: 'wedding_booked',
};

// Environment constants
const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
};

const SIMPLE_ANALYTICS_GLOBAL = 'sa_event';

// Helper: Check if running in browser
function isClient() {
  return typeof window !== 'undefined';
}

// Helper: Check if PostHog is loaded
function hasPostHog() {
  return isClient() && typeof window.posthog !== 'undefined';
}

function ensureSimpleAnalyticsStub() {
  if (!isClient()) return;

  const dispatcher = window[SIMPLE_ANALYTICS_GLOBAL];
  if (typeof dispatcher === 'function') {
    return;
  }

  const queue = [];
  const stub = (...args) => {
    queue.push(args);
  };

  stub.q = queue;
  window[SIMPLE_ANALYTICS_GLOBAL] = stub;
}

function capturePostHogEvent(eventName, payload) {
  if (!hasPostHog()) return;

  try {
    window.posthog.capture(eventName, payload);
  } catch (error) {
    if (typeof window.posthog?.captureException === 'function') {
      try {
        window.posthog.captureException(error);
      } catch (_captureError) {
        // ignore - best effort only
      }
    }
    // Silently fail - analytics should never break the app
  }
}

function captureSimpleAnalyticsEvent(eventName, payload) {
  if (!isClient()) return;

  ensureSimpleAnalyticsStub();

  const dispatcher = window[SIMPLE_ANALYTICS_GLOBAL];
  if (typeof dispatcher !== 'function') return;

  try {
    dispatcher(eventName, payload);
  } catch (error) {
    // Silently fail - analytics should never break the app
  }
}

/**
 * Detect environment based on hostname
 * Dev: *.myshopify.com, localhost, 127.0.0.1
 * Prod: Everything else (your actual domain)
 */
function getEnvironment() {
  if (!isClient()) return ENVIRONMENTS.PRODUCTION;

  const hostname = window.location.hostname;

  if (
    hostname.includes('.myshopify.com') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.includes('ngrok')
  ) {
    return ENVIRONMENTS.DEVELOPMENT;
  }

  return ENVIRONMENTS.PRODUCTION;
}

function getPathname() {
  if (!isClient()) return '/';

  const pathname = window.location.pathname;
  return pathname || '/';
}

/**
 * Track an event with automatic environment tagging
 * @param {string} eventName - Event name from ANALYTICS_EVENTS
 * @param {Object} properties - Additional event properties
 */
function track(eventName, properties = {}) {
  const eventProps = {
    ...properties,
    page_path: getPathname(),
  };

  capturePostHogEvent(eventName, eventProps);
  captureSimpleAnalyticsEvent(eventName, {
    ...eventProps,
    environment: getEnvironment(),
  });
}

/**
 * Track product-related events with standardized properties
 * @param {string} eventName - Event name
 * @param {Object} productData - Product information
 */
function trackProduct(eventName, productData = {}) {
  track(eventName, {
    product_id: productData.id ?? null,
    product_title: productData.title ?? null,
    product_price: productData.price ?? null,
    product_variant_id: productData.variantId ?? null,
    product_variant_title: productData.variantTitle ?? null,
    quantity: productData.quantity ?? 1,
  });
}

/**
 * Initialize analytics - register environment as super property
 */
function init() {
  if (!hasPostHog()) {
    return;
  }

  try {
    const env = getEnvironment();
    window.posthog.register({
      environment: env,
    });
  } catch (error) {
    // Silently fail - analytics should never break the app
  }
}

// Auto-initialize when DOM is ready
if (isClient()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

// Export as global for use in Shopify theme
if (isClient()) {
  window.SnowcubesAnalytics = {
    EVENTS: ANALYTICS_EVENTS,
    ENVIRONMENTS: ENVIRONMENTS,
    track: track,
    trackProduct: trackProduct,
    getEnvironment: getEnvironment,
    init: init,
  };
}
